import React from 'react';
import { cn } from '../utils/cn';

interface SpinnerProps {
  /** Size of the spinner */
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /** Whether to animate (for page loading) or static (for status badges) */
  animate?: boolean;
  /** Custom className */
  className?: string;
  /** Use page loader animation (slow-accelerate-decelerate) */
  pageLoader?: boolean;
}

const sizeClasses = {
  sm: 'w-4 h-4',
  md: 'w-6 h-6',
  lg: 'w-8 h-8',
  xl: 'w-12 h-12',
};

/**
 * Refresh-style spinner icon
 * - Static by default for status indicators
 * - Animated for loading states
 * - Page loader mode with special easing animation
 */
export const Spinner: React.FC<SpinnerProps> = ({
  size = 'md',
  animate = false,
  className,
  pageLoader = false,
}) => {
  const animationClass = pageLoader
    ? 'spinner-page-loader'
    : animate
      ? 'animate-spin'
      : '';

  return (
    <svg
      className={cn(
        sizeClasses[size],
        animationClass,
        'spinner-icon', // For theme-based coloring
        className
      )}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {/* Double-arrow refresh/sync style spinner */}
      <path d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
    </svg>
  );
};

/**
 * Full page loading spinner with centered positioning
 */
export const PageLoader: React.FC<{ text?: string }> = ({
  text = 'Loading...',
}) => {
  return (
    <div className="flex flex-col items-center justify-center min-h-[200px] page-loader">
      <Spinner size="xl" pageLoader className="mb-4" />
      {text && <p className="text-ink-subtle text-sm">{text}</p>}
    </div>
  );
};

export default Spinner;
